// src/components/driver_group/DriverGroupForm.js
import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  CircularProgress,
  Paper,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { getCompanyTariffs } from '../../api/tariffAPI';

// Define validation schema - made DriverTariffId required
const schema = yup.object({
  DriversGroupName: yup.string().required('Driver group name is required'),
  DriversGroupEnabled: yup.boolean(),
  DriversGroupDiscountId: yup.number().nullable().transform(value => 
    isNaN(value) || value === '' ? null : value
  ),
  DriverTariffId: yup.number().required('Tariff selection is required').transform(value => 
    isNaN(value) || value === '' ? null : value
  ),
}).required();

const DriverGroupForm = ({ driverGroup, discounts = [], companyId, onSubmit, isLoading }) => {
  // Fetch tariffs for this company - get all tariffs (both enabled and disabled)
  const { data: tariffs } = useQuery({
    queryKey: ['company-tariffs', companyId],
    queryFn: () => getCompanyTariffs(companyId), // Don't pass enabled parameter to get all
    enabled: !!companyId
  });

  const { control, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: driverGroup ? {
      DriversGroupName: driverGroup.DriversGroupName,
      DriversGroupEnabled: driverGroup.DriversGroupEnabled,
      DriversGroupDiscountId: driverGroup.DriversGroupDiscountId || '',
      DriverTariffId: driverGroup.DriverTariffId || '',
    } : {
      DriversGroupName: '',
      DriversGroupEnabled: true,
      DriversGroupDiscountId: '',
      DriverTariffId: '',
    }
  });

  const onFormSubmit = (data) => {
    // Add company ID to the form data for driver group creation
    const formData = {
      ...data,
      DriversGroupCompanyId: companyId,
    };
    
    // Convert empty strings to null except for required DriverTariffId
    Object.keys(formData).forEach(key => {
      if (formData[key] === '' && key !== 'DriverTariffId') {
        formData[key] = null;
      }
    });
    
    onSubmit(formData);
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Grid container spacing={3} direction="column">
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Basic Information</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="DriversGroupName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Driver Group Name"
                  fullWidth
                  error={!!errors.DriversGroupName}
                  helperText={errors.DriversGroupName?.message}
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="DriversGroupEnabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Driver Group Enabled"
                />
              )}
            />
          </Grid>

          {/* Tariff Assignment - Now Required */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Tariff Assignment</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="DriverTariffId"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.DriverTariffId}>
                  <InputLabel id="tariff-select-label">Tariff *</InputLabel>
                  <Select
                    {...field}
                    labelId="tariff-select-label"
                    label="Tariff *"
                    required
                  >
                    <MenuItem value="">
                      <em>Select a tariff</em>
                    </MenuItem>
                    {tariffs && tariffs.map((tariff) => (
                      <MenuItem key={tariff.TariffsId} value={tariff.TariffsId}>
                        {tariff.TariffsName} ({tariff.TariffsType || 'No type'}) - {tariff.TariffsEnabled ? 'Enabled' : 'Disabled'}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.DriverTariffId && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.DriverTariffId.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          {/* Discount Assignment - Optional */}
          {discounts.length > 0 && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Discount Assignment (Optional)</Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="DriversGroupDiscountId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel id="discount-select-label">Discount</InputLabel>
                      <Select
                        {...field}
                        labelId="discount-select-label"
                        label="Discount"
                        error={!!errors.DriversGroupDiscountId}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {discounts.map((discount) => (
                          <MenuItem key={discount.DiscountId} value={discount.DiscountId}>
                            {discount.DiscountName} ({discount.DiscountAmount}%)
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : null}
              >
                {driverGroup ? 'Update' : 'Create'} Driver Group
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default DriverGroupForm;